
import React, { useState, useEffect, useRef } from 'react';
import PlusIcon from './icons/PlusIcon';
import MinusIcon from './icons/MinusIcon';
import { getTranslation } from '../utils/translations';
import { formatQuantityDisplay, parseQuantityInput, snapToDisplayFraction } from '../utils/fractionQuantity';
import type { Language } from '../types';

interface QuantityCapsuleProps {
    value: number;
    unit: string;
    onUpdate?: (newValue: number) => void;
    language?: Language;
    isMeat?: boolean;
    bgClass?: string;
    hideUnitDisplay?: boolean;
    step?: number;
    min?: number;
    max?: number;
}

const QuantityCapsule: React.FC<QuantityCapsuleProps> = ({ 
    value, unit, onUpdate, language, isMeat, bgClass, hideUnitDisplay = false,
    step = 0.5, min = 0, max = 999
}) => {
    const t = (k: any) => getTranslation(language, k);
    const [isOpen, setIsOpen] = useState(false);
    const [inputText, setInputText] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            setInputText(formatQuantityDisplay(value));
        }
    }, [isOpen, value]);

    // Handle clicking outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            // Ensure focus on mount of open state
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select();
                }
            }, 10);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const displayValue = formatQuantityDisplay(value);

    const commitInput = () => {
        const parsed = parseQuantityInput(inputText);
        if (parsed !== null && onUpdate) {
            onUpdate(snapToDisplayFraction(Math.max(min, Math.min(max, parsed))));
        } else {
            setInputText(formatQuantityDisplay(value));
        }
    };

    // READ-ONLY / CLOSED STATE
    if (!onUpdate || !isOpen) {
        return (
            <div 
                onClick={(e) => {
                    if (onUpdate) {
                        e.stopPropagation();
                        setIsOpen(true);
                    }
                }}
                className={`
                    flex items-center justify-center rounded-lg px-3 py-1 transition-all min-w-[3rem] h-8 gap-1 select-none
                    ${!onUpdate ? 'cursor-default' : 'cursor-pointer hover:shadow-sm'}
                    ${bgClass ? bgClass : (isMeat 
                        ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-900 hover:bg-rose-100 dark:hover:bg-rose-900/50' 
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700')}
                `}
            >
                <span className="text-xs font-black font-mono">{displayValue}</span>
                {!hideUnitDisplay && unit && (
                    <span className="opacity-60 text-[9px] uppercase tracking-wider">{t('unit_' + unit.toLowerCase())}</span>
                )}
            </div>
        );
    }

    // OPEN (EDIT) STATE
    return (
        <div 
            ref={containerRef}
            onClick={(e) => e.stopPropagation()}
            className="w-32 h-9 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-full shadow-xl flex items-center justify-between z-10 overflow-hidden ring-2 ring-slate-100 dark:ring-slate-800 animate-fade-in"
        >
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); onUpdate(Math.max(min, parseFloat((value - step).toFixed(2)))); }}
                className="w-9 h-full flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 active:bg-slate-200 transition-colors text-slate-400 dark:text-slate-500 hover:text-red-500 border-r border-slate-100 dark:border-slate-800"
            >
                <MinusIcon className="w-4 h-4" />
            </button>
            
            <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onBlur={commitInput}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        commitInput();
                        setIsOpen(false);
                    }
                }}
                className="w-full h-full text-center bg-transparent border-none focus:ring-0 p-0 text-sm font-black text-slate-800 dark:text-slate-100 font-mono"
            />
            
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); onUpdate(Math.min(max, parseFloat((value + step).toFixed(2)))); }}
                className="w-9 h-full flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 active:bg-slate-200 transition-colors text-slate-400 dark:text-slate-500 hover:text-emerald-500 border-l border-slate-100 dark:border-slate-800"
            >
                <PlusIcon className="w-4 h-4" />
            </button>
        </div>
    );
};

export default QuantityCapsule;
